import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Globe, ChevronDown } from "lucide-react";
import { useI18n, useCanonicalPath } from "@/contexts/I18nContext";

/**
 * Region/language selector — header pill with flag + code.
 * Opens a popover with all supported regions. Selection is persisted to
 * localStorage + cookie (handled inside I18nContext).
 */
export const REGIONS = [
  { code: "sq", country: "Shqipëria",      language: "Shqip",     flag: "AL", title: "Albania"       },
  { code: "en", country: "United Kingdom", language: "English",   flag: "GB", title: "United Kingdom"},
];

/** Reusable flag image from FlagCDN (vector). */
export function Flag({ code, className = "" }) {
  return (
    <img
      src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
      alt={code}
      className={`inline-block h-4 w-5 rounded-[3px] object-cover ring-1 ring-black/[0.06] ${className}`}
      loading="lazy"
      decoding="async"
    />
  );
}

export default function LanguageSelector({ compact = false }) {
  const { lang, setLang, t } = useI18n();
  const canonicalPath = useCanonicalPath();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = REGIONS.find((r) => r.code === lang) || REGIONS[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function choose(code) {
    setLang(code);
    setOpen(false);
    // Navigate to the same page in the picked language — keeps URLs indexable.
    const target =
      code === "sq"
        ? (canonicalPath === "/" ? "/" : canonicalPath)
        : (canonicalPath === "/" ? "/en" : `/en${canonicalPath}`);
    navigate(target);
  }

  return (
    <div className="relative" ref={ref} data-testid="lang-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-[#0F172A] transition-colors hover:border-[#CBD5E1]"
        data-testid="lang-toggle"
      >
        <Flag code={current.flag} />
        {!compact && <span className="uppercase tracking-wider">{current.code}</span>}
        <ChevronDown size={11} className={`text-[#94A3B8] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-[280px] overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_24px_60px_-20px_rgba(11,18,32,0.25)]"
          role="listbox"
          data-testid="lang-popover"
        >
          <div className="flex items-center gap-2 border-b border-[#F1F5F9] bg-[#FAFBFC] px-4 py-3">
            <Globe size={13} className="text-[#EA580C]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#475569]">
              {t.common.chooseRegion}
            </span>
          </div>
          <ul className="max-h-[360px] overflow-auto py-1.5">
            {REGIONS.map((r) => {
              const active = r.code === lang;
              return (
                <li key={r.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => choose(r.code)}
                    className={`group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${active ? "bg-[#FFF1E5]" : "hover:bg-[#F8FAFC]"}`}
                    data-testid={`lang-option-${r.code}`}
                  >
                    <Flag code={r.flag} className="h-5 w-7" />
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-[13px] font-semibold ${active ? "text-[#EA580C]" : "text-[#0F172A]"}`}>
                        {r.country}
                      </div>
                      <div className="truncate text-[11px] text-[#64748B]">{r.language}</div>
                    </div>
                    {active && (
                      <Check size={14} className="shrink-0 text-[#F26322]" strokeWidth={2.4} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
